
import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import type { Application } from '@/types/database.types';

interface MedicalQuestionnaireProps {
  data: Partial<Application>;
  onSave: (field: string, value: any) => Promise<void>;
}

// Reusable Yes/No component
const YesNoRadioGroup = ({ 
  field, 
  value, 
  label, 
  onChange 
}: { 
  field: string; 
  value: boolean | null | undefined; 
  label: string; 
  onChange: (field: string, value: boolean) => Promise<void>;
}) => (
  <div className="space-y-2 mb-4">
    <Label>{label}</Label>
    <RadioGroup 
      value={value === true ? "yes" : value === false ? "no" : undefined}
      onValueChange={(val) => onChange(field, val === "yes")} 
      className="flex space-x-4"
    >
      <div className="flex items-center space-x-2">
        <RadioGroupItem value="yes" id={`${field}-yes`} />
        <Label htmlFor={`${field}-yes`}>Yes</Label>
      </div>
      <div className="flex items-center space-x-2">
        <RadioGroupItem value="no" id={`${field}-no`} />
        <Label htmlFor={`${field}-no`}>No</Label>
      </div>
    </RadioGroup>
  </div>
);

// Condition component with additional details
const ConditionQuestion = ({
  field,
  value,
  label,
  onChange,
  detailsField,
  detailsValue,
  onDetailsChange
}: {
  field: string;
  value: boolean | null | undefined;
  label: string;
  onChange: (field: string, value: boolean) => Promise<void>;
  detailsField?: string;
  detailsValue?: string | null;
  onDetailsChange?: (field: string, value: string) => Promise<void>;
}) => (
  <div className="space-y-2 mb-4">
    <YesNoRadioGroup
      field={field}
      value={value}
      label={label}
      onChange={onChange}
    />
    
    {value === true && detailsField && onDetailsChange && (
      <div className="ml-6 mt-2">
        <Label htmlFor={detailsField}>Please provide details</Label>
        <Textarea
          id={detailsField}
          value={detailsValue || ''}
          onChange={(e) => onDetailsChange(detailsField, e.target.value)}
          placeholder="Enter details"
          className="mt-1"
        />
      </div>
    )}
  </div>
);

export const MedicalQuestionnaire = ({ data, onSave }: MedicalQuestionnaireProps) => {
  // Function to handle field changes - now returns Promise
  const handleChange = async (field: string, value: any) => {
    // Store changes in application data for "Save & Continue" button
    await onSave(field, value);
  };

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-medium mb-2">Medical Conditions</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Please indicate if any employees or dependents covered under your health plan have any of the following conditions:
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          <div>
            <YesNoRadioGroup
              field="has_kidney_disease"
              value={data.has_kidney_disease}
              label="Kidney Disease"
              onChange={handleChange}
            />
            
            <YesNoRadioGroup
              field="has_diabetes"
              value={data.has_diabetes}
              label="Diabetes"
              onChange={handleChange}
            />
            
            <YesNoRadioGroup
              field="has_liver_disease"
              value={data.has_liver_disease}
              label="Liver Disease"
              onChange={handleChange}
            />
            
            <YesNoRadioGroup
              field="has_neural_disorder"
              value={data.has_neural_disorder}
              label="Neurological Disorder"
              onChange={handleChange}
            />
          </div>
          
          <div>
            <YesNoRadioGroup
              field="has_cardiovascular"
              value={data.has_cardiovascular}
              label="Cardiovascular Disease"
              onChange={handleChange}
            />
            
            <YesNoRadioGroup
              field="has_respiratory_disease"
              value={data.has_respiratory_disease}
              label="Respiratory Disease"
              onChange={handleChange}
            />
            
            <YesNoRadioGroup
              field="has_musculoskeletal"
              value={data.has_musculoskeletal}
              label="Musculoskeletal Disorder"
              onChange={handleChange}
            />
            
            <YesNoRadioGroup
              field="has_transplants"
              value={data.has_transplants}
              label="Organ Transplants"
              onChange={handleChange}
            />
          </div>
        </div>
      </div>
      
      <Separator />
      
      <div className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          <div>
            <YesNoRadioGroup
              field="has_cancer"
              value={data.has_cancer}
              label="Cancer"
              onChange={handleChange}
            />
            
            <YesNoRadioGroup
              field="has_hiv_aids"
              value={data.has_hiv_aids}
              label="HIV/AIDS"
              onChange={handleChange}
            />
            
            <YesNoRadioGroup
              field="has_mental_health"
              value={data.has_mental_health}
              label="Mental Health Conditions"
              onChange={handleChange}
            />
            
            <YesNoRadioGroup
              field="has_nervous_system"
              value={data.has_nervous_system}
              label="Nervous System Disorders"
              onChange={handleChange}
            />
          </div>
          
          <div>
            <YesNoRadioGroup
              field="has_other_conditions"
              value={data.has_other_conditions}
              label="Other Serious Conditions"
              onChange={handleChange}
            />
            
            {data.has_other_conditions && (
              <div className="ml-6 mt-2 mb-4">
                <Label htmlFor="other_conditions_details">Please describe the conditions</Label>
                <Textarea
                  id="other_conditions_details"
                  value={data.other_conditions_details || ''}
                  onChange={(e) => handleChange('other_conditions_details', e.target.value)}
                  placeholder="Enter details about other conditions"
                />
              </div>
            )}
            
            <YesNoRadioGroup
              field="has_premature_birth"
              value={data.has_premature_birth}
              label="Premature Births"
              onChange={handleChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
